import { Account, BackofficePersona } from '@/domain/usecases'
import { FeatureFlagType } from '@/infra/feature-flag'
import { PathConstants } from '../../paths'
import {
  composeGuards,
  Guard,
  GuardContext,
  GuardResult,
  requirePersona,
  requireFeatureFlag
} from '../../shared/guards'

// ============================================
// GUARDS DO MÓDULO BACKOFFICE
// Acesso por PERSONA (conceito de domínio) para os perfis administrativos.
// `requirePersona` deriva de Account.getPersonas (membership), preservando o
// comportamento das antigas checagens role-a-role:
//   COMPANY_ADMIN ⟸ company manager   |   INTERNAL ⟸ internal company manager
// System manager segue gate por role (não compõe persona).
// ============================================

/**
 * Exige que o usuário tenha a persona COMPANY_ADMIN (company manager / "RH").
 */
const requireCompanyManager: Guard = requirePersona(BackofficePersona.COMPANY_ADMIN)

/**
 * Exige que o usuário tenha a persona INTERNAL (internal company manager).
 */
const requireInternalCompanyManager: Guard = requirePersona(BackofficePersona.INTERNAL)

/**
 * Exige que o usuário tenha qualquer persona de backoffice
 * (COMPANY_ADMIN ou INTERNAL). Use em rotas acessíveis pelos dois públicos.
 */
const requireAnyCompanyManager: Guard = requirePersona(
  BackofficePersona.COMPANY_ADMIN,
  BackofficePersona.INTERNAL
)

/**
 * Exige que o usuário seja um System Manager.
 * System manager não compõe audiência — gate por role dedicado.
 */
function requireSystemManager(ctx: GuardContext): GuardResult {
  if (Account.isSystemManager(ctx.account)) {
    return { allowed: true }
  }

  console.log('[Guard] User is not a system manager')
  return {
    allowed: false,
    redirect: PathConstants.Routes.HOME.value,
    reason: 'User is not a system manager'
  }
}

// ============================================
// GUARDS COMPOSTOS DO MÓDULO BACKOFFICE
// ============================================

export const backofficeGuards = {
  /**
   * Exige a persona COMPANY_ADMIN (company manager).
   */
  companyManager: requireCompanyManager,

  /**
   * Exige a persona INTERNAL (internal company manager).
   */
  internalCompanyManager: requireInternalCompanyManager,

  /**
   * Exige que o usuário seja um system manager.
   */
  systemManager: requireSystemManager,

  /**
   * Exige qualquer persona de backoffice (company manager ou internal).
   * Use em rotas acessíveis pelos dois públicos.
   */
  anyCompanyManager: requireAnyCompanyManager,

  /**
   * Exige persona COMPANY_ADMIN com uma feature flag específica.
   */
  companyManagerWithFeature: (flag: FeatureFlagType): Guard =>
    composeGuards(requireCompanyManager, () => requireFeatureFlag(flag)),

  /**
   * Exige persona INTERNAL com uma feature flag específica.
   * Se a flag estiver desabilitada, redireciona para a fallback URL informada (HOME por padrão).
   */
  internalCompanyManagerWithFeature: (flag: FeatureFlagType, fallbackUrl?: string): Guard =>
    composeGuards(requireInternalCompanyManager, () => requireFeatureFlag(flag, fallbackUrl))
}
